import type { DocumentTemplateUpdateFailedAction } from '@Application/Documents/Templates/Actions/documentTemplateUpdateFailed';
import UpdateDocumentTemplate from '@Application/Documents/Templates/Commands/UpdateDocumentTemplate';
import updateDocumentTemplate from '@Application/Documents/Templates/updateDocumentTemplate';
import documentTemplateEditSidebarSignal, {
	closeDocumentTemplateEditSidebar,
} from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateEditSidebarSignal';
import DocumentTemplateEditFormData from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateEditSidebar/FormData/DocumentTemplateEditFormData';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar, TextArea } from '@verclam/ui-kit';

/**
 * Edits a template's name and description only — the document type and the file never change here.
 * Reads the edited template straight off `documentTemplatesStore.templates` rather than through a
 * prop: the screen mounts this component once, with no data of its own to pass it.
 */
const DocumentTemplateEditSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const templateId = useSignalValue(documentTemplateEditSidebarSignal);
	const { currentOrganization } = useCurrentOrganization();
	const templates = useSelector((state) => state.documentTemplatesStore.templates);

	const [formData, setFormData] = React.useState(() => DocumentTemplateEditFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();

	const editedTemplate = React.useMemo(() => {
		return templates?.findById(templateId) ?? null;
	}, [templates, templateId]);

	React.useEffect(() => {
		if (templateId === null) {
			return;
		}

		setFormData(
			editedTemplate !== null
				? DocumentTemplateEditFormData.fromView(editedTemplate)
				: DocumentTemplateEditFormData.createEmpty(),
		);
	}, [templateId, editedTemplate]);

	const handleNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setName(value));
	}, []);

	const handleDescriptionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setDescription(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: the organization screen never mounts this sidebar without a current
		// organization, and its signal only opens with a template id.
		if (currentOrganization === null || templateId === null) {
			return;
		}

		const description = formData.description.trim() === '' ? null : formData.description;

		setIsSubmitting(true);

		dispatch(
			updateDocumentTemplate(
				new UpdateDocumentTemplate(currentOrganization.id, templateId, formData.name, description),
			),
		)
			.then(() => {
				closeDocumentTemplateEditSidebar();
			})
			.catch((action: DocumentTemplateUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'DOCUMENT_TEMPLATE_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, currentOrganization, templateId, dispatch]);

	return (
		<Sidebar
			visible={templateId !== null}
			onHide={closeDocumentTemplateEditSidebar}
			position="right"
			header="Modifier le gabarit"
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeDocumentTemplateEditSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label="Modifier un gabarit">
				<InputText
					name="name"
					label="Nom du gabarit"
					required
					value={formData.name}
					onChange={handleNameChange}
					errorMessage={formData.getValidationMessage('name')}
					isFormFieldValid={formData.isValid('name')}
				/>
				<TextArea
					name="description"
					label="Description"
					value={formData.description}
					onChange={handleDescriptionChange}
					errorMessage={formData.getValidationMessage('description')}
					isFormFieldValid={formData.isValid('description')}
					rows={12}
				/>
			</Form>
		</Sidebar>
	);
};

export default DocumentTemplateEditSidebar;
